/**
 * `RuntimeState.record` — the run's own view of the Record (D241): the
 * layers it began with, and the layers its endings have written since.
 *
 * @remarks
 * The Record is an INPUT to a run, like its seed: the host hands it in at
 * genesis, and the run copies it here, so a reload mid-run reads the Record
 * as it was when the run began and a replay reproduces every `record_holds`
 * from `(bundle, seed, Record, commands)`. Conditions read only
 * `heldAtGenesis`; that is what "read once, at genesis" means, and why a
 * death inside a run can never make a tell appear in that same run.
 *
 * `writtenThisRun` is the other half: each ending the pack lists in
 * `record.writtenBy` adds its layer here the turn it is reached (folded, as
 * every Record is). The engine stores nothing anywhere — the host compares
 * a turn's `before` and `after` and keeps what was written
 * (`recordedLayersOf`), and the in-game `restart` begins the next run on both
 * halves folded together, exactly as the host would.
 *
 * Neither half is the world's: the checkpoint never photographs this
 * sub-state, so a return to the checkpoint rewinds nothing here — the death
 * that sent the player back has written its layer, and it stays written.
 * Nothing here is ever spoken, shown, or exported.
 *
 * @packageDocumentation
 */

import { z } from 'zod';

import { recordLayerSchema, recordLayersSchema } from '../record.js';

/** The run's view of the Record — see the module remarks. */
export const recordViewStateSchema = z
	.strictObject({
		/** The Record as it stood when this run began — what every `record_holds` reads. */
		heldAtGenesis: recordLayersSchema,
		/** The layers this run's endings have written, folded — for the host to keep. */
		writtenThisRun: z.array(recordLayerSchema).readonly(),
	})
	.readonly();

/** The `record` sub-state — see {@link recordViewStateSchema}. */
export type RecordViewState = z.infer<typeof recordViewStateSchema>;
